// ConceptMap — React Flow canvas with dark theme and concept node interactions
//
// Renders the concept map in DialogLayout's left panel. Receives nodes/edges
// from useConceptMap (already in React Flow format). Handles node drag
// (pinning) and fitView on changes.

import { useCallback, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useReactFlow,
  ReactFlowProvider,
  type Node,
  type Edge,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTranslation } from 'react-i18next';
import ConceptMapNode from './ConceptMapNode';

interface ConceptMapProps {
  nodes: Node[];
  edges: Edge[];
  isLoading: boolean;
  onNodeDragStop: (nodeId: string, position: { x: number; y: number }) => void;
  readOnly?: boolean;
  /** Called when the user clicks a node (only fires when not readOnly) */
  onNodeClick?: (nodeId: string, nodeLabel: string) => void;
}

const nodeTypes: NodeTypes = {
  conceptMapNode: ConceptMapNode,
};

function ConceptMapInner({ nodes, edges, isLoading, onNodeDragStop, readOnly, onNodeClick }: ConceptMapProps) {
  const { t } = useTranslation('session');
  const { fitView } = useReactFlow();
  const prevNodeCountRef = useRef(nodes.length);

  // fitView when nodes are added
  useEffect(() => {
    if (nodes.length > prevNodeCountRef.current) {
      const timer = setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 50);
      return () => clearTimeout(timer);
    }
    prevNodeCountRef.current = nodes.length;
  }, [nodes.length, fitView]);

  const handleNodeDragStop = useCallback(
    (_event: unknown, node: Node) => {
      onNodeDragStop(node.id, node.position);
    },
    [onNodeDragStop],
  );

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-sm animate-pulse">{t('conceptMap.loading')}</div>
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <p className="text-sm">{t('conceptMap.emptyState')}</p>
      </div>
    );
  }

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodeDragStop={readOnly ? undefined : handleNodeDragStop}
      onNodeClick={
        onNodeClick && !readOnly
          ? (_event, node) => onNodeClick(node.id, (node.data as { label: string }).label)
          : undefined
      }
      nodesDraggable={!readOnly}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      proOptions={{ hideAttribution: true }}
      className="h-full bg-gray-950"
      minZoom={0.3}
      maxZoom={2}
    >
      <Background color="#374151" gap={20} size={1} />
      {!readOnly && (
        <Controls
          showInteractive={false}
          className="!bg-gray-800 !border-gray-700 !shadow-lg [&>button]:!bg-gray-800 [&>button]:!border-gray-700 [&>button]:!text-gray-400 [&>button:hover]:!bg-gray-700"
        />
      )}
    </ReactFlow>
  );
}

export default function ConceptMap(props: ConceptMapProps) {
  return (
    <ReactFlowProvider>
      <ConceptMapInner {...props} />
    </ReactFlowProvider>
  );
}
